import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { formatPrice, calcDiscount } from '../../utils/formatPrice';
import { ShoppingBagIcon } from '@heroicons/react/24/outline';
import ProductRating from '../reviews/ProductRating';
import OfferBadge from '../offers/OfferBadge';

const HeartSvg = ({ filled }) => (
  <svg className="w-4 h-4" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
  </svg>
);

const ProductCard = ({ product, style = {} }) => {
  const { addItem }                     = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const [imgLoaded,  setImgLoaded]      = useState(false);
  const [addState,   setAddState]       = useState('idle'); // idle | adding | done
  const [heartAnim,  setHeartAnim]      = useState(false);

  // Use market-specific price when available, otherwise fall back to base price
  const displayPrice    = product.marketPrice ?? product.price;
  const currencySymbol  = product.marketCurrencySymbol ?? null;
  const formatDisplayPrice = (amount) =>
    currencySymbol ? `${currencySymbol}${Number(amount).toLocaleString()}` : formatPrice(amount);

  const discount   = calcDiscount(displayPrice, product.compareAtPrice);
  const wishlisted = isWishlisted(product.id);
  const continueOOS = product.variants?.some(v => v.continueSellingWhenOutOfStock);
  const isOOS      = (product.inventory ?? 0) <= 0 && !continueOOS;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    if (addState !== 'idle') return;
    setAddState('adding');
    await new Promise(r => setTimeout(r, 400));
    addItem(product, product.variants[0]?.size || 'default');
    toast.success(`${product.title.split(' ').slice(0, 4).join(' ')} added!`, {
      style: { borderRadius: '10px' },
    });
    setAddState('done');
    setTimeout(() => setAddState('idle'), 1800);
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    setHeartAnim(true);
    setTimeout(() => setHeartAnim(false), 350);
    toggleWishlist(product);
  };

  return (
    <article
      className="group bg-white overflow-hidden card-hover relative flex flex-col rounded-2xl border border-[#EDEAF2]"
      style={{ isolation: 'isolate', ...style }}
    >
      {/* ── Badges: only the offer and real flags; the discount sits quietly next to the price ── */}
      <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
        <OfferBadge product={product} />
        {product.isNew && (
          <span className="badge bg-white/90 text-primary border border-primary/15 text-[10px] tracking-[0.12em] px-2 py-0.5">
            NEW
          </span>
        )}
        {product.isBestseller && (
          <span className="badge bg-white/90 text-primary border border-primary/15 text-[10px] tracking-[0.12em] px-2 py-0.5">
            BESTSELLER
          </span>
        )}
      </div>

      {/* ── Wishlist button ── */}
      <button
        onClick={handleWishlist}
        title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        className={`absolute top-3 right-3 z-10 w-8 h-8 rounded-full flex items-center justify-center transition-all
          ${wishlisted ? 'bg-primary text-white' : 'bg-white/90 text-ink-soft border border-[#EDEAF2] hover:text-primary'}
          ${heartAnim ? 'animate-heart-pop' : ''}`}
      >
        <HeartSvg filled={wishlisted} />
      </button>

      {/* ── Image: one soft tone behind every photo (white photo backgrounds blend into it) ── */}
      <Link to={`/product/${product.handle}`} className="block relative overflow-hidden bg-[#F6F4F9] aspect-square">
        {!imgLoaded && <div className="absolute inset-0 shimmer" />}
        <img
          src={product.images[0]}
          alt={product.title}
          loading="lazy"
          onLoad={() => setImgLoaded(true)}
          className={`w-full h-full object-contain p-5 mix-blend-multiply group-hover:scale-[1.04] transition-transform duration-500
            ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
        />
      </Link>

      {/* ── Info body ── */}
      <div className="flex flex-col flex-1 px-3.5 md:px-4 pt-3.5 pb-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-muted mb-1.5">
          {product.vendor}
        </p>

        <Link to={`/product/${product.handle}`} className="flex-1">
          <h3 className="text-[13px] md:text-sm font-medium text-ink line-clamp-2 leading-snug mb-2.5 group-hover:text-primary transition-colors">
            {product.title}
          </h3>
        </Link>

        {/* Rating */}
        <ProductRating productId={product.id} className="mb-2" />

        {/* Price, with the discount beside it */}
        <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5 mb-3">
          <span className="text-[15px] md:text-base font-semibold text-ink">{formatDisplayPrice(displayPrice)}</span>
          {product.compareAtPrice > displayPrice && (
            <span className="text-xs text-ink-muted line-through">{formatPrice(product.compareAtPrice)}</span>
          )}
          {discount > 0 && (
            <span className="text-[11px] font-semibold text-emerald-700">{discount}% off</span>
          )}
        </div>

        <button
          onClick={handleAddToCart}
          disabled={isOOS || addState === 'adding'}
          className={`w-full h-9 rounded-full text-[13px] font-semibold flex items-center justify-center gap-1.5
            transition-all duration-200 active:scale-[.97] mt-auto
            ${isOOS
              ? 'text-gray-400 cursor-not-allowed bg-gray-50'
              : addState === 'done'
              ? 'bg-emerald-600 text-white'
              : 'bg-primary-50 text-primary hover:bg-primary hover:text-white'
            }`}
        >
          {addState === 'adding' ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              Adding…
            </>
          ) : addState === 'done' ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
              </svg>
              Added!
            </>
          ) : isOOS ? (
            'Out of Stock'
          ) : (
            <><ShoppingBagIcon className="w-4 h-4" aria-hidden="true" />Add to Cart</>
          )}
        </button>
      </div>
    </article>
  );
};

export default ProductCard;
